import React, {Component} from "react"
import {flatten, get, round} from "lodash"
import {withRouter} from "react-router-dom"
import PropTypes from "prop-types"
import {Col, Row} from "react-bootstrap"
import qs from "qs"

import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"
import history from "@irati/utils/history"
import {
  RewardAndPaymentOrders,
  RewardCatalogueProduct,
  RewardCatalogueReward,
  RewardOrder,
  RewardWallet,
  User,
  Poll,
  Recognition,
  RecognitionSource
} from "@irati/types"
import {FormattedMessage} from "@irati/utils/lang"
import StepIndicator from "@irati/ui/src/StepIndicator"
import Banner from "@irati/ui/src/Banner"
import RewardService from "@irati/reward/apollo/Reward"
import RecognitionService from "@irati/recognition/services/Recognition"
import PaymentService from "@irati/payments/services/Payments"
import rewardBannerImg from "@irati-web/recognition/src/images/banner.png"
import Button from "@irati/ui/src/Button"
import Card from "@irati/ui/src/Layout/Card"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import Loading from "@irati/ui/src/Loading"
import {rewardTracking} from "@irati-tracking/reward"
import ErrorText from "@irati/ui/src/ErrorText"
import PaymentForm from "@irati-web/payments/src/components/PaymentForm"
import {getDomain} from "@irati/utils/links"
import endpoints from "@irati/payments/endpoints"
import rewardWalletTracking from "@irati-tracking/wallet/rewardWallet"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import {Strong} from "@irati/ui/src/Labels"
import {returnUserTextWithAndOrComma} from "@irati/wallet/helpers"

import ConfirmationPopup from "../components/ConfirmationPopup"
import Recipients from "../components/Recipients"
import Message from "../components/Message"
import Checkout from "../components/NewCheckout"
import RecipientList from "../components/Checkout/Steps/Recipients/RecipientList"
import {getTotal} from "../utils"
import {
  CardHeader,
  Grid,
  Hr,
  Icon,
  List,
  RowContent,
  Summary,
  SummaryButtons,
  SummaryRow,
  TitleContainer,
  TotalRow
} from "../components/styled"
import {StepNames, PaymentType} from "../enums"
import getCurrencyForReward from "../../../../shared/rewards/helpers/reward/currency"

const ADD_REWARD_FIRST_STEP = StepNames.Checkout
const STEPS = [
  {name: StepNames.Recipients, label: <FormattedMessage id="reward.steps.recipients.label" />},
  {name: StepNames.Message, label: <FormattedMessage id="reward.steps.message.label" />},
  {name: StepNames.Checkout, label: <FormattedMessage id="reward.steps.checkout.label" />, createOrderNow: true},
  {name: StepNames.Payment, label: <FormattedMessage id="reward.steps.payment.label" />}
]
const orderCreationStep = STEPS.findIndex((step) => step.name === StepNames.Checkout)
const paymentStep = STEPS.findIndex((step) => step.name === StepNames.Payment)

interface Props extends WithAuth {}

interface PartialPaymentOrder {
  uuid: string,
  currency: string
}

export interface MessageDetails {
  sharingPreferences: string,
  message: string,
  companyValues: string[]
}

interface State {
  rewardOrder?: RewardOrder,
  product: RewardCatalogueProduct | null,
  reward: RewardCatalogueReward | null,
  customRewardName?: string,
  messageDetails?: MessageDetails,
  creatingReward: Promise<RewardAndPaymentOrders> | null,
  paymentOrder?: PartialPaymentOrder,
  addRewardEntityType?: string,
  addRewardEntity?: Recognition | Poll,
  rewardEntityId?: string,
  recognitionId?: string,
  selectedOccasion?: string,
  rewardWallet: RewardWallet | {},
  recipients: User[],
  paymentMethod: PaymentType,
  firstStep: number,
  currentStep: number,
  stepsCount: number,
  paymentError: string,
  updateMessageTracked: boolean,
  showConfirmationPopup: boolean
}

class CreateReward extends Component<Props, State> {
  static contextTypes = {
    appPath: PropTypes.string.isRequired
  }

  state: State = {
    firstStep: 0,
    currentStep: 0,
    recipients: [],
    messageDetails: {
      sharingPreferences: "private",
      companyValues: [],
      message: ""
    },
    paymentMethod: PaymentType.NEW_CARD,
    stepsCount: STEPS.length,
    creatingReward: null,
    reward: null,
    product: null,
    paymentError: "",
    rewardWallet: {},
    updateMessageTracked: false,
    showConfirmationPopup: false
  }

  componentDidMount() {
    const {auth} = this.props
    // Identify and initialise flow based on route state:
    // Create Reward params
    const product = get(this.props, "location.state.product")
    const reward = get(this.props, "location.state.reward")
    // Add Reward params
    const type = get(this.props, "location.state.type")
    const item = get(this.props, "location.state.item")
    // CustomReward params
    const customRewardName = get(this.props, "location.state.customRewardName")

    const isCreatingReward = product && reward
    const isAddingReward = type && item

    if (isCreatingReward || isAddingReward || customRewardName) {
      const state = {...this.state}
      if (customRewardName) {
        state.customRewardName = customRewardName
      } else if (isAddingReward) {
        state.firstStep = STEPS.findIndex((step) => step.name === ADD_REWARD_FIRST_STEP)
        state.currentStep = state.firstStep
        state.stepsCount = STEPS.length - state.firstStep
        state.addRewardEntityType = type
        state.addRewardEntity = item
        state.selectedOccasion = get(item, "occasion.uuid")
        if (type === "poll") {
          state.recipients = item.winners.results.map((winner) => winner.user)
          state.messageDetails.message = item.desc
        } else {
          state.recipients = item.receivers.results
          state.messageDetails.message = item.message
        }
      } else {
        state.reward = reward
        state.product = product
      }
      if (get(auth.productPermissions, "wallet.rewardWallet")) {
        RewardService.getWallet().then((wallet) => {
          state.rewardWallet = wallet
          this.setState(() => state)
        })
      } else {
        this.setState(() => state)
      }
    } else {
      history.push({pathname: "/reward"}, this.context)
    }
  }

  setProduct = (product: RewardCatalogueProduct | null = null) => {
    // reset creatingReward if previous call thrown error
    this.setState({product, creatingReward: null})
  }

  setReward = (reward: RewardCatalogueReward | null = null) => {
    if (!reward) {
      // no reward / no product
      // reset creatingReward if previous call errored
      this.setState({reward, product: null, creatingReward: null})
    } else {
      this.setState({reward})
    }
  }

  setRecipients = (recipients: User[]) => {
    this.setState({recipients})
  }

  setMessageDetails = (messageDetails: MessageDetails) => {
    const {reward} = this.state
    if (reward && reward.isCredit && !this.state.updateMessageTracked) {
      this.setState({updateMessageTracked: true})
      rewardWalletTracking.updateMessage()
    }
    this.setState({messageDetails})
  }

  setPaymentMethod = (paymentMethod: PaymentType) => {
    const {firstStep} = this.state
    this.setState({
      paymentMethod,
      stepsCount: STEPS.length - firstStep
    })
  }

  setOccasion = (selectedOccasion: string) => {
    this.setState({selectedOccasion})
  }

  prevStep = () => {
    this.cleanError()
    const {currentStep, firstStep} = this.state
    if (currentStep === 1) {
      rewardWalletTracking.backToRecipient()
    }
    if (currentStep === 2) {
      rewardWalletTracking.backToMessage()
    }
    if (currentStep > firstStep) {
      this.setPrevStep()
    }
  }

  setPrevStep = () => {
    const {currentStep} = this.state
    this.setState({currentStep: currentStep - 1})
    window.scrollTo(0, 0)
  }

  setNextStep = () => {
    const {currentStep} = this.state
    this.setState({currentStep: currentStep + 1})
    window.scrollTo(0, 0)
  }

  isButtonDisabled = () => {
    const {currentStep, recipients, messageDetails, reward, product, customRewardName, rewardWallet, paymentMethod} = this.state
    const costTotal = getTotal(product, recipients.length)
    const availableWalletBalance = get(rewardWallet, "availableBalance") || 0
    const enoughCredit = availableWalletBalance >= costTotal
    switch (currentStep) {
      case 0:
        return !recipients.length
      case 1:
        if (messageDetails?.message && messageDetails.message.length > 280) {
          return true
        }
        if (messageDetails?.message && messageDetails.message.length < 281) {
          return false
        }
        return !messageDetails?.message
      case 2:
        if (customRewardName) return false
        if (paymentMethod === PaymentType.REWARD_WALLET && !enoughCredit) return true
        return !reward || !product
      default:
        return true
    }
  }

  handleTracking = () => {
    const {currentStep, recipients, messageDetails, reward} = this.state
    const {companyValues} = messageDetails
    switch (currentStep) {
      case 0:
        rewardTracking.rewardsTabAddRewardAddRecipient(recipients)
        if (get(reward, "isCredit")) {
          rewardWalletTracking.clickContinueToMessage()
        }
        break
      case 1:
        rewardTracking.rewardsTabAddRewardAddMessage()
        rewardTracking.rewardsTabAddRewardAddCompanyValues({companyValues})
        if (get(reward, "isCredit")) {
          rewardWalletTracking.clickContinueToCheckout()
        }
        break
      default:
        break
    }
  }

  nextStep = () => {
    this.cleanError()
    const {currentStep, paymentMethod, reward} = this.state
    if (currentStep < orderCreationStep) {
      this.setNextStep()
    }
    if (currentStep === orderCreationStep) {
      if (paymentMethod === PaymentType.REWARD_WALLET && get(reward, "isCredit")) {
        this.setState({showConfirmationPopup: true})
      } else {
        this.createOrder()
      }
    }
    this.handleTracking()
  }

  createOrder = () => {
    const {paymentMethod, customRewardName, reward, rewardWallet} = this.state
    const creatingReward = this.createReward()
      .then(({rewardOrder, paymentOrder}: RewardAndPaymentOrders) => {
        this.setState(() => ({paymentOrder, rewardOrder}))
        if (customRewardName) {
          return RecognitionService.updateRecognitionWithRewardId(this.state.recognitionId!, rewardOrder.uuid).then(() => {
            rewardTracking.rewardsTabAddCustomRewardSuccess({custom: customRewardName})
            history.push({pathname: `/reward/success/${rewardOrder.uuid}`}, this.context)
          })
        }

        if (paymentMethod === PaymentType.REWARD_WALLET) {
          // TODO RewardWallet doesn't support credit as a reward yet, we have to use the old wallet transfer for that.
          // This will go away once the backend will develop that functionality
          if (get(reward, "isCredit")) {
            rewardWalletTracking.clickPay()
            return RewardService.sendCreditAsReward(rewardWallet.uuid, rewardOrder.uuid).then(() => {
              return RecognitionService.updateRecognitionWithRewardId(this.state.recognitionId!, rewardOrder.uuid).then(() => {
                rewardTracking.rewardsTabCreditSend()
                history.push({pathname: `/reward/success/${rewardOrder.uuid}`}, this.context)
              })
            })
          }
          return this.payByRewardWallet()
        }
      })
    this.setState({creatingReward})
    this.setNextStep()
  }

  handleHide = () => {
    this.setState({showConfirmationPopup: false})
  }

  createReward = () => {
    const {
      reward,
      product,
      recipients,
      messageDetails,
      customRewardName,
      addRewardEntity,
      addRewardEntityType,
      selectedOccasion
    } = this.state
    const {
      auth:{
        business,
        user: {
          teamIds: userTeamIds
        }
      }
    } = this.props

    const recipientIds = recipients.map((user) => user.uuid)

    const rewardPayload = {
      tenantId: business?.uuid,
      rewardId: reward?.uuid,
      productId: product?.uuid,
      recipientUserIds: recipientIds,
      message: messageDetails?.message,
      custom: customRewardName,
      isCreditReward: reward?.isCredit
    }

    this.paymentCompleted = false

    if (!addRewardEntity) {
      const recognitionPayload = {
        companyValues: messageDetails?.companyValues,
        teamIds: flatten(recipients.map(({teamIds}) => teamIds)).concat(userTeamIds),
        userIds: recipientIds,
        isPrivate: messageDetails?.sharingPreferences === "private",
        message: messageDetails?.message,
        source: RecognitionSource.Perkbox,
        occasionId: selectedOccasion,
        expectReward: true
      }

      return RecognitionService.create(recognitionPayload)
        .then(({uuid: recognitionId}: Recognition) => {
          this.setState({recognitionId})
          return RewardService.create(rewardPayload)
        })
    }

    if (addRewardEntityType === "poll") {
      rewardPayload.poll_id = addRewardEntity.uuid
    } else {
      rewardPayload.recognition_id = addRewardEntity.uuid
    }

    this.setState({rewardEntityId: addRewardEntity.uuid})

    return RewardService.create(rewardPayload)
  }

  payByRewardWallet = () => {
    const {rewardOrder, paymentOrder} = this.state
    const amount = {amount: round(rewardOrder!.costTotal.amount * 100)}
    return PaymentService.payByRewardWallet(paymentOrder, amount, rewardOrder).then(this.paymentComplete)
  }

  paymentCompleted = false

  paymentComplete = () => {
    if (this.paymentCompleted) return

    this.paymentCompleted = true
    const {rewardOrder, reward, recipients, recognitionId, rewardEntityId, addRewardEntityType} = this.state
    const rewardOrderId = rewardOrder!.uuid

    return RewardService.confirmRewardPaymentOrder(rewardOrderId!)
      .then((): Promise<Poll | Recognition | void> => {
        if (rewardEntityId && addRewardEntityType === "poll") {
          return RecognitionService.updatePollWithRewardId(rewardEntityId, rewardOrderId)
        }
        return RecognitionService.updateRecognitionWithRewardId(rewardEntityId || recognitionId!, rewardOrderId)
      })
      .then(() => {
        const recipientsCount = recipients.length
        const rewardTotal = rewardOrder!.costTotal
        const rewardHeader = reward!.header
        rewardTracking.rewardsTabAddRewardSuccess(recipientsCount, rewardTotal, rewardHeader)
        history.push({pathname: `/reward/success/${rewardOrderId}`}, this.context)
      })
      .catch(() => {
        // Payment failed, we aren't getting any code from BE of why it failed, posting generic message
        this.setState({paymentError: "reward.genericPaymentError"})
        this.setPrevStep()
      })
  }

  cleanError = () => {
    this.setState({paymentError: ""})
  }

  getPaymentRedirectUrl = () => {
    const {rewardOrder, recognitionId, rewardEntityId, addRewardEntityType} = this.state
    const {auth:{business:{subdomain}}} = this.props

    const domain = getDomain(subdomain)
    const entityType = addRewardEntityType || "recognition"
    const entityId = rewardEntityId || recognitionId
    const query = qs.stringify({
      redirectTo: `${domain}/recognition/reward/complete-payment/${rewardOrder!.uuid}/${entityType}/${entityId}`
    })
    return `${endpoints.adyenRedirectCallbackUrl()}?${query}`
  }

  buildUsers = () => {
    const {recipients} = this.state
    return recipients
      .map(this.userToNameAndEmail)
  }

  userToNameAndEmail = (user: User, index: number) => {
    const {recipients} = this.state
    const text = returnUserTextWithAndOrComma(
      recipients.length,
      index,
      "reward.sendCreditAsReward.confirmationPopup.text1.user",
      "reward.sendCreditAsReward.confirmationPopup.text1.userWithComma",
      "reward.sendCreditAsReward.confirmationPopup.text1.userWithAnd"
    )

    return (
      <div className="text-center" key={user.uuid}>
        <FormattedMessage
          id={text}
          values={{
            name: firstnameLastname(user),
            email: user.email
          }} />
      </div>
    )
  }

  handleConfirmation = () => {
    this.handleHide()
    this.createOrder()
  }

  render() {
    const {
      reward,
      product,
      recipients,
      messageDetails,
      currentStep,
      creatingReward,
      paymentOrder,
      rewardOrder,
      paymentMethod,
      stepsCount,
      customRewardName,
      firstStep,
      rewardWallet,
      paymentError,
      addRewardEntity,
      selectedOccasion,
      showConfirmationPopup
    } = this.state
    const bannerImage = get(reward, "bannerImage")
    const isCredit = get(reward, "isCredit", false)
    let buttonLabel = "reward.detail.btnContinue.label"
    if (currentStep === orderCreationStep) {
      if (paymentMethod === PaymentType.REWARD_WALLET) {
        buttonLabel = "reward.confirmation.button.title"
      } else if (customRewardName) {
        buttonLabel = "reward.customReward.summary.button"
      }
    }
    const currencyCode = getCurrencyForReward(reward!)
    const amount = product && formatCurrency({value: getTotal(product, recipients.length), isCents: false, currencyCode})

    return (
      <>
        <Banner bannerImg={bannerImage || rewardBannerImg} />
        <Grid>
          <Row>
            <Col md={8}>
              <Card>
                <CardHeader>
                  <TitleContainer>
                    <h4>{STEPS[currentStep].label}</h4>
                    <StepIndicator currentStep={currentStep - firstStep + 1} stepsCount={stepsCount} />
                  </TitleContainer>
                </CardHeader>
                {currentStep === 0 && <Recipients recipients={recipients} setRecipients={this.setRecipients} isCredit={isCredit} />}

                {currentStep === 1 && (
                  <Message
                    recipients={recipients}
                    messageDetails={messageDetails}
                    selectedOccasion={selectedOccasion}
                    setOccasion={this.setOccasion}
                    setMessageDetails={this.setMessageDetails}
                    isCredit={isCredit} />
                )}

                {currentStep === 2 && (
                  <Checkout
                    product={product}
                    reward={reward}
                    customRewardName={customRewardName}
                    recipientsCount={recipients.length}
                    setPaymentMethod={this.setPaymentMethod}
                    addRewardEntity={addRewardEntity}
                    selectedOccasion={selectedOccasion}
                    setOccasion={this.setOccasion}
                    paymentMethod={paymentMethod}
                    setProduct={this.setProduct}
                    setReward={this.setReward}
                    rewardWallet={rewardWallet}
                    isCredit={isCredit} />
                )}

                {currentStep === 3 && (
                  <Loading load={creatingReward}>
                    {rewardOrder && !customRewardName && (
                      <PaymentForm
                        paymentOrder={paymentOrder}
                        amount={Math.round(rewardOrder.costTotal.amount * 100)}
                        onComplete={this.paymentComplete}
                        redirectReturnUrl={this.getPaymentRedirectUrl()} />
                    )}
                  </Loading>
                )}
              </Card>
            </Col>
            <Col md={4}>
              <Summary>
                <h4>
                  <FormattedMessage id="reward.summary.title" />
                </h4>

                {(!!recipients.length || messageDetails?.message) && (
                  <SummaryRow>
                    <RowContent>
                      <List>
                        <RecipientList recipients={recipients} />
                      </List>
                    </RowContent>
                  </SummaryRow>
                )}
                {messageDetails?.message && (
                  <SummaryRow>
                    <Icon icon="heartOutline" size={23} />
                    <RowContent>{messageDetails.message}</RowContent>
                  </SummaryRow>
                )}
                <Hr />

                {customRewardName && (
                  <SummaryRow>
                    <Icon icon="customReward" size={23} />
                    <p>{customRewardName}</p>
                  </SummaryRow>
                )}
                {product && (
                  <SummaryRow>
                    <Icon icon="reward" size={23} />
                    <p>{get(product, "product.name")}</p>
                  </SummaryRow>
                )}

                {product && (
                  <TotalRow>
                    <h5>
                      <FormattedMessage id="reward.summary.totalToPay" />
                    </h5>
                    <h4>{amount}</h4>
                  </TotalRow>
                )}

                {currentStep < paymentStep && (
                  <Loading load={creatingReward}>
                    <SummaryButtons>
                      {currentStep > firstStep && (
                        <Button secondary onClick={this.prevStep}>
                          <FormattedMessage id="reward.summary.back.label" />
                        </Button>
                      )}
                      <Button primary disabled={this.isButtonDisabled()} onClick={this.nextStep}>
                        <FormattedMessage id={buttonLabel} />
                      </Button>
                    </SummaryButtons>
                  </Loading>
                )}
                {paymentError && (
                  <ErrorText>
                    <FormattedMessage id={paymentError} />
                  </ErrorText>
                )}
              </Summary>
            </Col>
          </Row>
        </Grid>
        {!!recipients.length && product &&
          <ConfirmationPopup
            title="reward.sendCreditAsReward.confirmationPopup.title"
            topText={
              <>
                <FormattedMessage
                  id="reward.sendCreditAsReward.confirmationPopup.text1.top"
                  values={{credit: amount}} />
                {this.buildUsers()}
              </>

            }
            bottomText={<FormattedMessage
              id="reward.sendCreditAsReward.confirmationPopup.text2"
              values={{
                boldText: (
                  <Strong>
                    <FormattedMessage id="reward.sendCreditAsReward.confirmationPopup.text2Bold" />
                  </Strong>
                )
              }} />}
            show={showConfirmationPopup}
            onHide={this.handleHide}
            onClick={this.handleConfirmation}
            backButtonText="reward.sendCreditAsReward.confirmationPopup.backButton"
            continueButtonText="reward.sendCreditAsReward.confirmationPopup.sendButton" />
        }
      </>
    )
  }
}

export default withRouter(withAuth(CreateReward))
